import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {FollowUp} from '../../interfaces/follow-up.interface';
import {Observable, Subject} from 'rxjs';
import {OpportunityService} from '../../services/opportunity.service';
import {filter, take, takeUntil, tap} from 'rxjs/operators';
import {
    MatCell, MatCellDef,
    MatColumnDef,
    MatHeaderCell, MatHeaderCellDef,
    MatHeaderRow, MatHeaderRowDef,
    MatRow, MatRowDef,
    MatTable,
    MatTableDataSource
} from '@angular/material/table';
import {OpportunityStages} from '../../interfaces/opportunity-stages.enum';
import {MatPaginator} from '@angular/material/paginator';
import {Opportunity} from '../../interfaces/opportunity.interface';
import {AuthService} from '../../../services/auth.service';
import {MatDialog} from '@angular/material/dialog';
import {OpportunityDeletionDialogComponent} from '../opportunity-deletion-dialog/opportunity-deletion-dialog.component';
import {FollowUpEditDialogComponent} from '../follow-up-edit-dialog/follow-up-edit-dialog.component';
import {MatProgressBar} from '@angular/material/progress-bar';
import {MatIcon} from '@angular/material/icon';
import {TimestampPipe} from '../../../shared/pipes/timestamp.pipe';
import {AsyncPipe, CurrencyPipe, DatePipe, NgClass} from '@angular/common';
import {MatTooltip} from '@angular/material/tooltip';
import {MatIconButton} from '@angular/material/button';



@Component({
    selector: 'app-follow-ups-list',
    templateUrl: 'follow-ups-list.component.html',
    imports: [
        MatTable,
        MatHeaderRow,
        MatHeaderCell,
        MatCell,
        MatRow,
        MatPaginator,
        MatProgressBar,
        MatIcon,
        TimestampPipe,
        DatePipe,
        CurrencyPipe,
        MatTooltip,
        NgClass,
        AsyncPipe,
        MatColumnDef,
        MatIconButton,
        MatHeaderCellDef,
        MatCellDef,
        MatHeaderRowDef,
        MatRowDef
    ],
    styleUrls: [
        '../opportunities-list/opportunity-list.component.scss',
        './follow-ups-list.component.scss'
    ]
})
export class FollowUpsListComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() opportunity: Opportunity;
    @Output() followUpSelected: EventEmitter<FollowUp> = new EventEmitter<FollowUp>();

    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;

    list$: Observable<FollowUp[]>;
    datasource: MatTableDataSource<FollowUp> = new MatTableDataSource<FollowUp>();
    totalElements: number;

    displayedColumns = [
        'status',
        'opportunityStage',
        'salesManager',
        'nextSteps',
        'potentialClosure',
        'proposalSentOn',
        'proposalRequested',
        'followUpDate',
        'nextFollowUpDate',
        'forecastAmount',
        'actions'
    ];

    private unsubscribe$: Subject<void> = new Subject<void>();

    protected readonly OpportunityStages = OpportunityStages;

    selectedFollowUp: FollowUp | null = null;

    _canDeleteFollowUp = false;

    constructor(
        private opportunityService: OpportunityService,
        private authService: AuthService,
        private matDialog: MatDialog
    ) {
    }

    ngOnInit() {
        this.list$ = this.opportunityService.getFollowUpsList$(this.opportunity.id).pipe(
            takeUntil(this.unsubscribe$),
            tap(list => {
                this.datasource.data = list;
                this.totalElements = list.length;
                this.selectFollowUp(list.filter(fu => fu.latest)[0]);
            })
        );

        this.authService.userHasPermissions(
            [
                'app.opportunity.followup.delete'
            ])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((val) => {
                this._canDeleteFollowUp = val[0];
            });
    }

    ngAfterViewInit() {
        this.datasource.paginator = this.paginator;
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    selectFollowUp(fu: FollowUp) {
        if (this.selectedFollowUp?.id === fu?.id) {
            return;
        }
        this.followUpSelected.emit(fu);
        this.selectedFollowUp = fu;
    }

    followUpCanBeDeleted(fu: FollowUp) {
        return fu.latest &&
            !fu.dummy && (
                this.opportunity.salesManager === this.authService.currentUser.email ||
                this._canDeleteFollowUp
            );
    }

    deleteFollowUp(followUp: FollowUp) {
        this.matDialog.open(
            OpportunityDeletionDialogComponent,
            {disableClose: true, data: followUp}
        ).afterClosed().pipe(
            take(1),
            filter(res => res)
        ).subscribe(() => {
            this.opportunityService.deleteFollowUp(followUp)
                .then(res => console.log('Follow-up deleted successfully.'))
                .catch(error => console.error('error deleting follow-up', error));
        });
    }

    editFollowUp(followUp: FollowUp) {
        this.matDialog.open(
            FollowUpEditDialogComponent,
            {disableClose: true, data: {followUp, opportunity: this.opportunity}}
        );
    }
}
